<template>
    <section class="product-select">
        <header
            class="product-select__header"
            :class="{
                'product-select__header--soldout': isSoldOut && noPriceProvided,
                'product-select__header--large-text': (isSoldOut || noPriceProvided) && !arrivalDate
            }"
        >
            <Favorite :product="productDetails" class="product-select__favorite" />

            <slot name="header">
                <!--Sold out header-->
                <div v-if="isSoldOut && noPriceProvided" class="product-select__sold-title">
                    {{ $t('ProductDetails.productSelect.soldOut') }}
                </div>

                <!--Countdown header-->
                <ProductSelectCountdownHeader
                    v-else-if="productDetails.availableUntil"
                    :available-until="productDetails.availableUntil"
                    :back-button-visible="showCountdownBackButton"
                    @back="selectedVariant = null"
                    @expired="isExpired = true"
                />

                <!--Arrival date header-->
                <CalendarSelection
                    v-else-if="isDirectBookingDateShown"
                    :arrival-date="arrivalDate"
                    @back="backToCalendar"
                />

                <!-- No prices provided (multivoucher) -->
                <div v-else-if="noPriceProvided">
                    {{ $t('ProductDetails.productSelect.selectTrip') }}
                </div>

                <!--Header with product price-->
                <div v-else class="product-select__price-wrapper text-center">
                    <!-- Price prefix -->
                    <template v-if="!isOtherProduct">
                        <div v-if="isAccessCodeRequired" class="product-select__darker-text">
                            {{ $t('ProductDetails.productSelect.additionalHalfBoardPayment') }}
                        </div>
                    </template>

                    <ProductSelectHeaderPrice
                        :product-details="productDetails"
                        :is-value-voucher="isValueVoucher"
                        :is-other-product="isOtherProduct"
                        class="product-select__main-price"
                    />

                    <!-- Value voucher -->
                    <template v-if="isValueVoucher">
                        <div
                            v-if="productDetails.perPersonText"
                            class="product-select__darker-text product-select__accent product-select__per-person-text"
                        >
                            {{ productDetails.perPersonText }}
                        </div>

                        <div class="product-select__claim product-select__accent text-primary">
                            {{ productDetails.claim || $t('ProductDetails.productSelect.giftWithAnimod') }}
                        </div>
                    </template>
                </div>
            </slot>
        </header>

        <div class="product-select__body">
            <div class="checkout-available-from" v-if="productDetails.checkoutAvailableFrom">
                {{ $t('ProductSelect.checkoutAvailableFrom', { date: productDetails.checkoutAvailableFrom }) }}
            </div>
            <SoldOutSubscribe v-if="isSoldOut" :product-details="productDetails" taller-prefix />

            <ProductExpiredSubscribe v-else-if="isExpired" :product-details="productDetails" taller-prefix />

            <ProductSelectBiddingBody v-else-if="showBiddingForm" :selected-variant="selectedVariant" />

            <div v-else-if="isDirectBookingCalendar" style="min-height: 450px">
                <ProductSelectTitle>
                    {{ $t('ProductDetails.productSelect.selectWithArrivalDate') }}
                </ProductSelectTitle>

                <!-- Important! Do not server render calendar: it is very slow in node v8 and slows down the entire page ssr -->
                <client-only>
                    <Calendar
                        ref="calendar"
                        :selected="arrivalDate"
                        :available-dates="productDetails.variantsWithScheduledArrival"
                        @update:selected="dateSelect"
                        gradation-by-price
                    />
                </client-only>
            </div>

            <template v-else>
                <template v-if="!isOtherProduct">
                    <ProductSelectTitle v-if="isAccessCodeRequired">
                        <template v-if="isDelayPayment"> &nbsp;</template>
                        <template v-else>
                            {{ $t('ProductDetails.productSelect.selectWithCopayment') }}
                        </template>
                    </ProductSelectTitle>

                    <ProductSelectTitle v-else-if="!isUpselling">
                        {{
                            isValueVoucher
                                ? $t('ProductDetails.productSelect.selectVoucher')
                                : $t('ProductDetails.productSelect.selectOffer')
                        }}
                    </ProductSelectTitle>
                </template>

                <VariantsList
                    v-if="!isUpselling"
                    v-skeleton="!isEmptyDetails"
                    :variants="variants"
                    :arrivalDate="arrivalDate"
                    :isValueVoucher="isValueVoucher"
                    :isAuctionOffer="isAuctionOffer"
                    :class="{ 'mt-2': isOtherProduct }"
                    @variantSelected="onFlatVariantSelection"
                />

                <UpsellingOptions
                    v-else
                    :variants="variants"
                    :selected-variant="selectedVariant"
                    @variantSelected="onUpsellVariantSelection"
                />
            </template>

            <media-only desktop>
                <div class="text-center">
                    <AvailabilityPreview
                        v-if="productDetails.hasAvailabilityPreview || isEmployee"
                        :product-id="productDetails.id"
                        :selected-variant-id="selectedVariant && selectedVariant.id"
                        class="mt-3"
                    />
                    <span v-if="isEmployee" style="font-size:.7rem;color:#778899">{{productDetails.calendarPreviewInfo}}</span>
                </div>
            </media-only>

            <div
                v-if="hasOtherHotelProducts && !isUpselling"
                class="product-select__other-products-wrapper"
                :class="{ 'py-2': callsToActionVisible }"
            >
                <OtherHotelProducts class="py-3 px-5 text-muted" />
            </div>
            <div v-if="callsToActionVisible" class="product-select__additional-info">
                <VoucherProtection v-if="buyerProtectionEnabled" class="product-select__info-modal" />
                <ReservationSteps
                    v-if="!isOtherProduct && !isDirectBooking"
                    class="product-select__info-modal d-print-none"
                />
            </div>
        </div>

        <ProductSelectBottomFooter
            v-if="isUpselling && !isExpired"
            :selected-variant="selectedVariant"
            :arrivalDate="arrivalDate"
            :checkoutAvailableFrom="productDetails.checkoutAvailableFrom"
            @submit="gtmTrackVariantSelection"
        >
            <template v-if="isDirectBookingCalendar" #right>
                <div class="d-flex align-items-center justify-content-end">
                    <div class="mr-3">{{ $t('UpsellingFooter.bestPriceSecure') }}</div>
                    <div>
                        <button class="btn btn-primary btn-lg py-3 text-decoration-none" @click="scrollToCalendar">
                            {{ $t('UpsellingFooter.toDateSelection') }}
                        </button>
                    </div>
                </div>
            </template>
        </ProductSelectBottomFooter>
    </section>
</template>

<script>
import Price from '@/components/Price.vue';
import DiscountBadge from '@/components/DiscountBadge.vue';
import SoldOutSubscribe from '@/components/product/selection/SoldOutSubscribe.vue';
import Calendar from '@/components/product/Calendar.vue';
import VariantsList from '@/components/product/selection/VariantsList.vue';
import ReservationSteps from '@/components/guideSteps/ReservationSteps.vue';
import VoucherProtection from '@/components/VoucherProtection.vue';
import formatPriceFilter from '@/filters/formatPrice';
import skeleton from '@/directives/skeleton';
import productSelect from '@/mixins/productSelect';
import Favorite from '@/components/grid/Favorite.vue';
import UpsellingOptions from '@/components/product/selection/UpsellingOptions.vue';
import ProductSelectBottomFooter from '@/components/product/selection/ProductSelectBottomFooter.vue';
import ProductSelectHeaderPrice from '@/components/product/selection/ProductSelectHeaderPrice.vue';
import CalendarSelection from '@/components/product/selection/CalendarSelection.vue';
import ProductSelectTitle from '@/components/product/selection/ProductSelectTitle.vue';
import OtherHotelProducts from '@/components/product/selection/OtherHotelProducts.vue';
import AvailabilityPreview from '@/components/product/AvailabilityPreview.vue';
import ProductSelectCountdownHeader from '@/components/product/countdown/ProductSelectCountdownHeader.vue';
import ProductExpiredSubscribe from '@/components/product/countdown/ProductExpiredSubscribe.vue';
import ProductSelectBiddingBody from '@/components/product/auction/ProductSelectBiddingBody.vue';
import CountdownTimer from '@/components/product/countdown/CountdownTimer.vue';

export default {
    name: 'ProductSelect',
    filters: {
        formatPriceFilter
    },
    directives: {
        skeleton
    },
    mixins: [productSelect],
    components: {
        CountdownTimer,
        ProductSelectBiddingBody,
        ProductExpiredSubscribe,
        ProductSelectCountdownHeader,
        AvailabilityPreview,
        OtherHotelProducts,
        ProductSelectTitle,
        CalendarSelection,
        ProductSelectHeaderPrice,
        ProductSelectBottomFooter,
        UpsellingOptions,
        Favorite,
        Price,
        DiscountBadge,
        ReservationSteps,
        SoldOutSubscribe,
        Calendar,
        VariantsList,
        VoucherProtection
    },
    props: {
        buyerProtectionEnabled: Boolean,
        hasOtherHotelProducts: Boolean
    },
    methods: {
        scrollToCalendar() {
            const calendar = this.$refs.calendar;
            if (calendar) {
                calendar.$el.scrollIntoView();
            }
        }
    },
    computed: {
        isEmployee() {
            return this.$cookies.emp_id;
        },
        isEmptyDetails() {
            return !Object.keys(this.productDetails).length;
        },
        callsToActionVisible() {
            return (
                !this.isValueVoucher &&
                !this.productDetails.soldOut &&
                !this.isDirectBooking &&
                this.$store.state.theme !== 'kurzinurlaub' &&
                this.$store.state.theme !== 'wunschgutschein' &&
                !this.productDetails.webshopAccessCodeProduct &&
                (this.buyerProtectionEnabled || !this.isOtherProduct)
            );
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/_breakpoints.scss' as breakpoints;

.product-select {
    $self: &;

    &__header {
        min-height: 150px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        overflow: hidden;
        position: relative;
        margin: -1px -1px 0;
        padding: 15px;
        padding-bottom: 30px;

        &::after {
            background-color: var(--product-select-header-bg);
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            width: 300%;
            height: 300%;
            z-index: 2;
            border-bottom-left-radius: 100% 150%;
            border-bottom-right-radius: 100% 150%;
            transform: translateX(-50%);
        }

        &--soldout {
            color: hsl(0, 0%, 100%);

            &::after {
                background-color: variables.$soldout-color;
            }
        }

        &--large-text {
            font-size: 28px;
        }

        color: var(--product-select-price-color-main);

        #{$self}__darker-text {
            color: var(--product-select-price-color-darker);
        }

        & > * {
            z-index: 3;
        }
    }

    &__accent {
        font-size: 18px;
    }

    &__per-person-text {
        line-height: 2;
    }

    &__favorite {
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 4;
    }

    &__body {
        min-height: 80px;
    }

    &__additional-info {
        padding: 20px 0;
        display: flex;
        flex-direction: column;
    }

    &__info-modal {
        font-size: 14px;
        opacity: 0.8;
        cursor: pointer;
        font-weight: bold;
        padding: 5px 0;

        &:hover {
            opacity: 1;
        }
    }

    &__other-products-wrapper {
        margin-left: -1px;
        margin-right: -1px;
        border-bottom: 1px solid var(--border-color);
        background-color: var(--aside-darker-bg);
    }

    &__other-products-upselling {
        max-width: 250px;
        background-color: transparent;
        border: none;
        padding: 0;
    }

    .checkout-available-from {
        text-align: center;
        font-weight: bold;
        font-size: 1.2rem;
        color: #930202;
        margin: 10px 0;
        padding: 10px 0;
        border-top: 1px solid #930202;
        border-bottom: 1px solid #930202;
    }
}
</style>
